import { useContext, useEffect, useRef } from 'react';
import * as L from 'leaflet';
import 'leaflet.markercluster';
import { useStations } from 'domain/hooks/api/api';
import { CardListContext } from 'pages/base/CardList';

import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import { polygonize } from 'domain/utils/map.utils';

const ZOOM_VIEW_STATION = 14;

const DEFAULT_MAP_CENTER = {
	lat: 43,
	lng: -3.6,
	zoom: 8,
};

const markerIcon = L.icon({
	iconUrl: '/public/marker-icon.png',
	iconSize: [25, 41],
	iconAnchor: [12, 41],
	popupAnchor: [1, -34],
	shadowSize: [41, 41],
});

export const ObservationSide = () => {
	const { expanded: id, setExpanded } = useContext(CardListContext);

	const currentMap = useRef<any>(null);

	const markers = useRef<{ [id: string]: L.Marker<any> }>({});

	// On id change, update the map info
	const { find, findOne } = useStations();

	// Add all stations markers with the text (name) of the station
	useEffect(() => {
		if (!currentMap.current) {
			return;
		}

		find().then((stations) => {
			Object.values(markers.current).forEach((marker) => {
				currentMap.current.removeLayer(marker);
			});

			markers.current = {};

			const cluster = L.markerClusterGroup();

			const addStation = addStationMarker(cluster);
			stations.content.forEach(addStation);
			currentMap.current.addLayer(cluster);

			if (!id || id < 0) {
				// Show all the stations
				const group = L.featureGroup(Object.values(markers.current));
				currentMap.current.fitBounds(group.getBounds());
			}
		});
	}, [currentMap.current, find]);

	const addStationMarker =
		(cluster: L.MarkerClusterGroup) => (station: any) => {
			if (!currentMap.current) {
				return;
			}
			const marker = L.marker([station.latitude, station.longitude], {
				icon: markerIcon,
			});

			marker.bindPopup(station.name);
			// Add an id to the marker
			markers.current['station-' + station.id] = marker;

			marker.on('mouseover', function (e) {
				// @ts-ignore
				this.openPopup();
			});
			marker.on('mouseout', function (e) {
				// @ts-ignore
				this.closePopup();
			});

			// On click, set the station
			marker.on('click', () => {
				setExpanded(station.id);
			});

			cluster.addLayer(marker);
		};

	useEffect(() => {
		if (!id) {
			return;
		}
		if (id < 0) {
			return;
		}

		findOne('' + id).then((station) => {
			currentMap.current.setView(
				{
					lat: station.latitude,
					lng: station.longitude,
				},
				ZOOM_VIEW_STATION
			);
		});
	}, [id]);

	return (
		<BaseMap
			init={{
				refMap: currentMap,
			}}
		/>
	);
};

export type LatLng = {
	lat: number;
	lng: number;
};

export type Mark = LatLng & {
	id?: string;
	name?: string;
};

export type View = LatLng & {
	zoom?: number;
};

export type MapPolygon = {
	label?: string;
	points: number[];
};

export type ZoomProps = {
	zoom?: number;
	default?: {
		max?: number;
		min?: number;
	};
};

export const MapMarkers = ({
	marks,
	polygons,
	view,
	fit,
	zoom,
	onClick,
}: {
	marks?: {
		[id: string]: Mark;
	};
	polygons?: MapPolygon[];
	view?: View;
	fit?: 'all' | 'marks' | 'polygon' | [number, number];
	zoom?: ZoomProps;
	onClick?: (id: string) => void;
}) => {
	const currentMap = useRef<any>(null);

	fit = fit ?? 'all';
	const markers = useRef<{ [id: string]: L.Marker<any> }>({});

	// Add all stations markers with the text (name) of the station
	useEffect(() => {
		if (!currentMap.current) {
			return;
		}

		// Remove all the markers

		Object.values(markers.current).forEach((marker) => {
			currentMap.current.removeLayer(marker);
		});

		// Remove the previous polygon
		currentMap.current.eachLayer((layer: any) => {
			if (layer instanceof L.Polygon) {
				currentMap.current.removeLayer(layer);
			}
		});

		markers.current = {};

		const cluster = L.markerClusterGroup();

		const addStation = addMarker(cluster);

		const points: [number, number][] = [];

		if (marks) {
			Object.entries(marks).forEach(([id, mark]) => {
				addStation(id, {
					...mark,
				});
			});

			if (fit === 'marks' || fit === 'all') {
				// Add the points to center the map
				Object.values(marks).forEach((mark) => {
					points.push([mark.lat, mark.lng]);
				});
			}
		}

		if (polygons && polygons.length > 0) {
			const polygon = updatePolygons();

			if (fit === 'polygon' || fit === 'all') {
				// Add the points to center the map
				if (polygons.length === 0) {
					console.warn('No polygon');
				}
				if (polygon.length > 0) {
					polygon.forEach((p) => {
						points.push(...p);
					});
				}
			}
		}

		currentMap.current.addLayer(cluster);

		// Max zoom to 8 in order to avoid the map to be too much zoomed
		// Set the zoom to 8 if the zoom is bigger than 8

		// Center the map on the center of the polygon
		if (points.length > 0) {
			// Center the map on the center of the markers
			currentMap.current.fitBounds(points);

			// Check if zoom is set
			if (zoom) {
				if (zoom.default && zoom.default.max) {
					// Check if the zoom is bigger than the max
					if (currentMap.current.getZoom() > zoom.default.max) {
						currentMap.current.setZoom(zoom.default.max);
					}
				}
			}
		}
	}, [currentMap.current, marks, polygons, fit]);

	const updatePolygons = () => {
		if (!currentMap.current) {
			return [];
		}

		if (!polygons) {
			return [];
		}

		const pol = polygons.map((polygon) => polygonize(polygon.points));

		if (pol.length > 0) {
			const polygonLayer = L.polygon(pol, { color: 'red' });
			currentMap.current.addLayer(polygonLayer);

			return pol;
		}
		return [];
	};

	const addMarker =
		(cluster: L.MarkerClusterGroup) => (id: string, mark: Mark) => {
			if (!currentMap.current) {
				return;
			}
			const marker = L.marker([mark.lat, mark.lng], {
				icon: markerIcon,
			});

			marker.bindPopup(id);
			// Add an id to the marker
			markers.current[id] = marker;

			marker.on('mouseover', function (e) {
				// @ts-ignore
				this.openPopup();
			});
			marker.on('mouseout', function (e) {
				// @ts-ignore
				this.closePopup();
			});

			// On click, set the station
			marker.on('click', () => {
				if (!onClick) {
					return;
				}
				onClick(id);
			});

			cluster.addLayer(marker);
		};

	useEffect(() => {
		currentMap.current.setView(
			{
				lat: view?.lat ?? DEFAULT_MAP_CENTER.lat,
				lng: view?.lng ?? DEFAULT_MAP_CENTER.lng,
			},
			view?.zoom ?? DEFAULT_MAP_CENTER.zoom
		);
	}, [view]);

	return (
		<BaseMap
			init={{
				refMap: currentMap,
			}}
		/>
	);
};

/**
 * Create a Map container for reusing
 */
export type InitMap = {
	x?: number;
	y?: number;
	zoom?: number;
	refMap?: React.MutableRefObject<any>;
};

export const BaseMap = ({ init }: { init?: InitMap }) => {
	const leafletMapRef = useRef(null);

	const currentMap = useRef<any>(null);

	useEffect(() => {
		const current = leafletMapRef.current;
		if (!current) {
			return;
		}

		if (currentMap.current) {
			return;
		}

		const map = L.map(current);
		currentMap.current = map;
		// Set the URL to the tiles
		L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
			attribution:
				'Map data © <a href="https://openstreetmap.org">OpenStreetMap</a> contributors',
		}).addTo(map);

		// This simple trick is to force the map to resize
		const interval = setInterval(() => {
			window.dispatchEvent(new Event('resize'));
		}, 100);

		setTimeout(() => {
			clearInterval(interval);
		}, 1000);
	}, []);

	/**
	 * Pass the ref to the parent
	 */
	useEffect(() => {
		if (!init?.refMap) {
			return;
		}
		init.refMap.current = currentMap.current;
	}, [init?.refMap]);

	return (
		<div
			ref={leafletMapRef}
			className='leaflet-map position-relative h-full w-full'
		/>
	);
};
